/**
 * Toast — Notification banner
 *
 * A styled frame for toast notifications. State management
 * (show/hide/queue) is handled by the consuming app.
 *
 * Variants:
 *   - variant: success, error, info, warning
 */
import type { ReactNode } from 'react'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'

const ToastFrame = styled(XStack, {
  name: 'Toast',
  ai: 'center',
  gap: '$sm',
  px: '$md',
  py: '$sm',
  br: '$lg',
  mx: '$md',
  shadowColor: '$shadowColor',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 8,

  variants: {
    variant: {
      success: { bg: '$colorSuccess' },
      error: { bg: '$colorError' },
      info: { bg: '$colorInfo' },
      warning: { bg: '$colorWarning' },
    },
  } as const,

  defaultVariants: {
    variant: 'success',
  },
})

const ToastText = styled(TamaguiText, {
  name: 'ToastText',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '500',
  color: '$colorInverse',
  f: 1,
})

type ToastFrameProps = GetProps<typeof ToastFrame>

interface ToastProps extends ToastFrameProps {
  message: string
  icon?: ReactNode
  action?: ReactNode
}

export const Toast = ToastFrame.styleable<ToastProps>(
  ({ message, icon, action, variant, ...props }, ref) => {
    return (
      <ToastFrame ref={ref} variant={variant} {...props}>
        {icon}
        <ToastText>{message}</ToastText>
        {action}
      </ToastFrame>
    )
  },
)
